<template>
  <div class="space-y-6">
    <!-- Header -->
    <div class="text-center">
      <h2 class="text-3xl font-bold text-gray-900 dark:text-white">{{ $t('auth.welcome_back') }}</h2>
      <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">
        {{ $t('auth.sign_in_to_account') }}
      </p>
    </div>

    <!-- Login form -->
    <form @submit.prevent="handleLogin" class="space-y-5">
      <!-- Email -->
      <div>
        <label for="email" class="form-label text-gray-700 dark:text-gray-300">
          {{ $t('auth.email') }}
        </label>
        <div class="relative">
          <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <EnvelopeIcon class="h-5 w-5 text-gray-400 dark:text-gray-500" />
          </div>
          <input
            id="email"
            v-model="form.email"
            type="email"
            required
            class="form-input pl-10 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
            :class="{ 'border-red-500 focus:ring-red-500': errors.email }"
            placeholder="you@example.com"
          />
        </div>
        <p v-if="errors.email" class="mt-1 text-sm text-red-500">{{ errors.email[0] }}</p>
      </div>

      <!-- Password -->
      <div>
        <label for="password" class="form-label text-gray-700 dark:text-gray-300">
          {{ $t('auth.password') }}
        </label>
        <div class="relative">
          <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <LockClosedIcon class="h-5 w-5 text-gray-400 dark:text-gray-500" />
          </div>
          <input
            id="password"
            v-model="form.password"
            :type="showPassword ? 'text' : 'password'"
            required
            class="form-input pl-10 pr-10 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
            :class="{ 'border-red-500 focus:ring-red-500': errors.password }"
            placeholder="••••••••"
          />
          <button
            type="button"
            @click="showPassword = !showPassword"
            class="absolute inset-y-0 right-0 pr-3 flex items-center"
          >
            <EyeIcon v-if="!showPassword" class="h-5 w-5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
            <EyeSlashIcon v-else class="h-5 w-5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
          </button>
        </div>
        <p v-if="errors.password" class="mt-1 text-sm text-red-500">{{ errors.password[0] }}</p>
      </div>

      <!-- Remember me & Forgot password -->
      <div class="flex items-center justify-between">
        <div class="flex items-center">
          <input
            id="remember"
            v-model="form.remember"
            type="checkbox"
            class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
          />
          <label for="remember" class="ml-2 block text-sm text-gray-700 dark:text-gray-300">
            {{ $t('auth.remember_me') }}
          </label>
        </div>
        <router-link
          :to="{ name: 'forgot-password', params: { locale: currentLocale } }"
          class="text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-500 dark:hover:text-indigo-300 transition-colors"
        >
          {{ $t('auth.forgot_password') }}?
        </router-link>
      </div>

      <!-- Submit button -->
      <button
        type="submit"
        :disabled="isLoading"
        class="w-full py-3 px-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg font-semibold hover:shadow-lg transition-all duration-200 transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
      >
        <div v-if="isLoading" class="flex items-center justify-center">
          <div class="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
          {{ $t('common.messages.loading') }}
        </div>
        <span v-else class="flex items-center justify-center">
          {{ $t('auth.sign_in') }}
          <ArrowRightIcon class="ml-2 h-4 w-4" />
        </span>
      </button>
    </form>
  </div>
</template>

<script setup>
import { ref, reactive } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'
import { useToast } from 'vue-toastification'
import {
  EnvelopeIcon,
  LockClosedIcon,
  EyeIcon,
  EyeSlashIcon,
  ArrowRightIcon
} from '@heroicons/vue/24/outline'

const { t } = useI18n()

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const toast = useToast()

// Get current locale for links
const currentLocale = route.params.locale || 'en'

const form = reactive({
  email: '',
  password: '',
  remember: false
})

const errors = ref({})
const isLoading = ref(false)
const showPassword = ref(false)

const handleLogin = async () => {
  isLoading.value = true
  errors.value = {}

  try {
    // authStore.login handles redirect with locale automatically
    await authStore.login(form)
  } catch (error) {
    if (error.response?.status === 422) {
      errors.value = error.response.data.errors || {}
    } else if (error.response?.status === 401) {
      toast.error(t('auth.invalid_credentials'))
    } else {
      toast.error(t('common.messages.error'))
    }
  } finally {
    isLoading.value = false
  }
}
</script>
